import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Paper,
  TableContainer,
  Switch,
  FormControlLabel,
  MenuItem,
  Chip,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PublicIcon from "@mui/icons-material/Public";
import CategoryIcon from "@mui/icons-material/Category";
import api from "../../utils/api";

// ─── Shared styles ────────────────────────────────────────────────────────────

const paperSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  overflow: "hidden",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const dialogPaperSx = {
  borderRadius: "18px",
  background: "rgba(255,255,255,0.96)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  overflow: "hidden",
};

const fieldSx = {
  mt: 1.5,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.96)",
    borderRadius: "10px",
  },
};

const primaryButtonSx = {
  backgroundColor: "#2f63ad",
  color: "white",
  fontWeight: 700,
  borderRadius: "10px",
  textTransform: "none",
  px: 2.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
};

// ─── Constants ────────────────────────────────────────────────────────────────

const BILLING_MODE_LABELS = {
  flat: "Flat Fee",
  hourly: "Hourly",
  per_item: "Per Item",
};

const BILLING_MODE_COLORS = {
  flat: { bg: "rgba(47,99,173,0.1)", color: "#2f63ad" },
  hourly: { bg: "rgba(34,197,94,0.1)", color: "#16a34a" },
  per_item: { bg: "rgba(234,179,8,0.1)", color: "#b45309" },
};

const TRIGGER_MODE_LABELS = {
  manual: "Manual",
  automatic: "Automatic",
};

const BLANK_FORM = {
  scope: "global",
  architectural_type_id: "",
  integration_id: "",
  billing_mode: "flat",
  rate: "",
  labor_rate: "",
  trigger_mode: "manual",
  is_active: true,
  notes: "",
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ArchitecturalBillingSetupManager = () => {
  const [settings, setSettings] = useState([]);
  const [archTypes, setArchTypes] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [settingsRes, typesRes, intRes] = await Promise.all([
        api.get("/architectural-billing-settings"),
        api.get("/architectural-request-types"),
        api.get("/integrations"),
      ]);
      setSettings(settingsRes.data || []);
      setArchTypes(typesRes.data || []);
      setIntegrations(intRes.data || []);
    } catch {
      showSnackbar("Failed to load data", "error");
    }
  };

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const closeSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  const resetForm = () => {
    setForm(BLANK_FORM);
    setEditingId(null);
  };

  const openAdd = () => {
    resetForm();
    setOpenDialog(true);
  };

  const openEdit = (s) => {
    setEditingId(s.billing_setting_id);
    setForm({
      scope: s.architectural_type_id ? "type" : "global",
      architectural_type_id: s.architectural_type_id ?? "",
      integration_id: s.integration_id ?? "",
      billing_mode: s.billing_mode || "flat",
      rate: s.rate ?? "",
      labor_rate: s.labor_rate ?? "",
      trigger_mode: s.trigger_mode || "manual",
      is_active: Boolean(s.is_active ?? 1),
      notes: s.notes || "",
    });
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => {
      const next = { ...p, [name]: value };
      if (name === "scope" && value === "global") next.architectural_type_id = "";
      return next;
    });
  };

  const handleSubmit = async () => {
    if (form.rate === "") {
      showSnackbar("Please enter a rate", "error");
      return;
    }
    if (form.scope === "type" && !form.architectural_type_id) {
      showSnackbar("Please select an architectural type", "error");
      return;
    }

    const payload = {
      architectural_type_id:
        form.scope === "type" ? Number(form.architectural_type_id) : null,
      integration_id: form.integration_id ? Number(form.integration_id) : null,
      billing_mode: form.billing_mode,
      rate: Number(form.rate),
      labor_rate: form.labor_rate !== "" ? Number(form.labor_rate) : null,
      trigger_mode: form.trigger_mode,
      is_active: Boolean(form.is_active),
      notes: form.notes?.trim() || null,
    };

    try {
      if (editingId) {
        await api.put(`/architectural-billing-settings/${editingId}`, payload);
        showSnackbar("Billing rule updated");
      } else {
        await api.post("/architectural-billing-settings", payload);
        showSnackbar("Billing rule added");
      }
      await fetchAll();
      closeDialog();
    } catch {
      showSnackbar("Error saving billing rule", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this billing rule?")) return;
    try {
      await api.delete(`/architectural-billing-settings/${id}`);
      showSnackbar("Billing rule deleted");
      fetchAll();
    } catch {
      showSnackbar("Error deleting billing rule", "error");
    }
  };

  const getTypeLabel = (typeId) => {
    if (!typeId) return "All Types";
    return (
      archTypes.find((t) => Number(t.id) === Number(typeId))?.type || `Type #${typeId}`
    );
  };

  const getIntegrationLabel = (integrationId) => {
    if (!integrationId) return "N/A";
    return (
      integrations.find((i) => Number(i.id) === Number(integrationId))?.name ||
      `Integration #${integrationId}`
    );
  };

  const getRateLabel = (mode) => {
    if (mode === "hourly") return "Hourly Rate";
    if (mode === "per_item") return "Rate Per Item";
    return "Flat Rate";
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Architectural Billing Setup
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Configure billing rates and trigger behavior for architectural requests.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={primaryButtonSx}
        >
          Add Billing Rule
        </Button>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={paperSx}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Request Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Billing Mode</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Rate</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Labor Rate</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Trigger</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Integration</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settings.length > 0 ? (
                settings.map((s) => {
                  const mc =
                    BILLING_MODE_COLORS[s.billing_mode] || BILLING_MODE_COLORS.flat;
                  return (
                    <TableRow key={s.billing_setting_id}>
                      <TableCell>
                        <Chip
                          icon={s.architectural_type_id ? <CategoryIcon /> : <PublicIcon />}
                          label={s.architectural_type_id ? "Per Type" : "Global"}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            backgroundColor: "rgba(47,99,173,0.08)",
                            color: "#2f63ad",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getTypeLabel(s.architectural_type_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={BILLING_MODE_LABELS[s.billing_mode] || s.billing_mode}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            backgroundColor: mc.bg,
                            color: mc.color,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<AttachMoneyIcon />}
                          label={`$${Number(s.rate || 0).toFixed(2)}`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            backgroundColor: "rgba(34,49,66,0.08)",
                            color: "#223142",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {s.labor_rate ? (
                          <Typography variant="body2" sx={{ color: "#3a4a5c" }}>
                            ${Number(s.labor_rate).toFixed(2)}/hr
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: "#9aa5b4" }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            TRIGGER_MODE_LABELS[s.trigger_mode] || s.trigger_mode || "Manual"
                          }
                          size="small"
                          variant="outlined"
                          color={s.trigger_mode === "automatic" ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "#6b778c", fontSize: "0.82rem" }}
                        >
                          {getIntegrationLabel(s.integration_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={s.is_active ? "Active" : "Inactive"}
                          size="small"
                          color={s.is_active ? "success" : "default"}
                          variant={s.is_active ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openEdit(s)}>
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(s.billing_setting_id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{ color: "text.secondary", py: 4, textAlign: "center" }}
                  >
                    No architectural billing rules configured.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.3rem", pt: 3, pb: 1 }}
        >
          {editingId ? "Edit Billing Rule" : "Add Billing Rule"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          {/* Scope */}
          <TextField
            select fullWidth label="Rule Scope" name="scope"
            value={form.scope} onChange={handleChange} sx={fieldSx}
          >
            <MenuItem value="global">Global (all architectural requests)</MenuItem>
            <MenuItem value="type">Specific Request Type</MenuItem>
          </TextField>

          {form.scope === "type" && (
            <TextField
              select fullWidth label="Architectural Request Type"
              name="architectural_type_id"
              value={form.architectural_type_id} onChange={handleChange} sx={fieldSx}
            >
              <MenuItem value="">Select Type</MenuItem>
              {archTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>
              ))}
            </TextField>
          )}

          {/* Integration */}
          <TextField
            select fullWidth label="Integration" name="integration_id"
            value={form.integration_id} onChange={handleChange} sx={fieldSx}
            helperText="Optional. link to an accounting integration"
          >
            <MenuItem value="">None</MenuItem>
            {integrations.map((i) => (
              <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>
            ))}
          </TextField>

          {/* Billing mode */}
          <TextField
            select fullWidth label="Billing Mode" name="billing_mode"
            value={form.billing_mode} onChange={handleChange} sx={fieldSx}
          >
            {Object.entries(BILLING_MODE_LABELS).map(([val, label]) => (
              <MenuItem key={val} value={val}>{label}</MenuItem>
            ))}
          </TextField>

          {/* Rate */}
          <TextField
            fullWidth
            label={`${getRateLabel(form.billing_mode)} ($)`}
            name="rate" type="number" inputProps={{ min: 0, step: 0.01 }}
            value={form.rate} onChange={handleChange} sx={fieldSx}
          />

          {/* Labor rate */}
          <TextField
            fullWidth label="Additional Labor Rate ($/hr)" name="labor_rate"
            type="number" inputProps={{ min: 0, step: 0.01 }}
            value={form.labor_rate} onChange={handleChange} sx={fieldSx}
            helperText="Optional"
          />

          {/* Trigger mode */}
          <TextField
            select fullWidth label="Billing Trigger" name="trigger_mode"
            value={form.trigger_mode} onChange={handleChange} sx={fieldSx}
            helperText={
              form.trigger_mode === "automatic"
                ? "A billable record is created automatically on status change"
                : "A staff member manually reviews and exports billing"
            }
          >
            <MenuItem value="manual">Manual. staff reviews before export</MenuItem>
            <MenuItem value="automatic">Automatic. triggers on status change</MenuItem>
          </TextField>

          {/* Notes */}
          <TextField
            fullWidth label="Notes" name="notes" multiline rows={2}
            value={form.notes} onChange={handleChange} sx={fieldSx}
          />

          {/* Active */}
          <Box sx={{ mt: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.25 }}>
          <Button onClick={closeDialog} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" sx={primaryButtonSx}>
            {editingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArchitecturalBillingSetupManager;
